import {
	OrderQuoteProductSyncKind,
	OrderQuoteStatus,
	useDeleteOrderQuoteMutation,
	useGetOrderQuoteQuery,
	useGetOrderQuoteValueRangeQuery,
	useGetOrderQuoteViewSummaryQuery,
	usePreviewOrderQuoteProductSyncQuery,
	useRecordQuoteView,
	useRemoveOrderQuoteItemMutation,
	useUpdateOrderQuoteMutation,
	type GetOrderQuoteQuery,
} from '@codezo/graphql';
import { buildSpecificationRows } from '@codezo/product-specs';
import {
	Card,
	QuoteAddress,
	QuoteFacts,
	QuoteHeader,
	QuoteOverview,
	QuotePeople,
	QuoteSections,
	QuoteSectionsSkeleton,
	QuoteViews,
	RequestedProducts,
	SectionHead,
	type LifecycleStage,
	type QuoteLayoutSection,
	type QuoteStat,
	type RequestedProduct,
} from '@codezo/quote-detail';
import {
	ArrowLeft,
	ArrowUpRight,
	Copy,
	Eye,
	MoreHorizontal,
	Pencil,
	Plus,
	RefreshCw,
	Shield,
	Trash2,
	Upload,
	UserPlus,
} from 'lucide-react';
import { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { OrderQuoteOffers } from '../../../components/quotes/OrderQuoteOffers';
import { QuoteSuppliers } from '../../../components/quotes/suppliers/QuoteSuppliers';
import { ProductCommentsButton } from '../../../components/quotes/threads/ProductCommentsButton';
import { ProductThreadsPanel } from '../../../components/quotes/threads/ProductThreadsPanel';
import { useQuoteThreads } from '../../../components/quotes/threads/useQuoteThreads';
import { PendingAcceptanceReview } from '../../../components/quotes/PendingAcceptanceReview';
import { QuoteItemFiles } from '../../../components/quotes/QuoteItemFiles';
import { AddProductsToQuoteModal } from '../../../components/quotes/modals/AddProductsToQuoteModal';
import { DuplicateOrderQuoteModal } from '../../../components/quotes/modals/DuplicateOrderQuoteModal';
import { SyncProductFromCatalogueModal } from '../../../components/quotes/modals/SyncProductFromCatalogueModal';
import { EditOrderQuoteAddressModal } from '../../../components/quotes/modals/EditOrderQuoteAddressModal';
import { EditOrderQuoteDetailsModal } from '../../../components/quotes/modals/EditOrderQuoteDetailsModal';
import {
	EditOrderQuoteItemModal,
	type EditOrderQuoteItemValue,
} from '../../../components/quotes/modals/EditOrderQuoteItemModal';
import { AdminUserPickerModal } from '../../../components/selectors/AdminUserPickerModal';
import { VetterPickerModal } from '../../../components/selectors/VetterPickerModal';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { FilePreview, type FilePreviewItem } from '../../../components/ui/file-preview';
import { InitialsAvatar } from '../../../components/ui/initials-avatar';
import { ProductImage, orderedProductImages } from '../../../components/ui/product-image';
import { useGuestPreviewLink } from '../../../hooks/useGuestPreviewLink';
import { useSetPageMeta } from '../../../hooks/useSetPageMeta';
import { ORDER_QUOTE_STATUS } from '../../../lib/status-configs';
import { formatDate, timeAgo } from '../../../lib/utils';
import { useCurrencyStore } from '../../../stores/currency';
import { OfferBatchV2 } from './OfferBatchV2';
import { QuoteActivityV2 } from './QuoteActivityV2';
import { QuoteClientV2 } from './QuoteClientV2';
import { QuoteClientContactsV2 } from './QuoteClientContactsV2';
import { QuoteSettingsV2 } from './QuoteSettingsV2';
import { formatPercentRange, useCompactMoneyRange } from './format';

type Quote = NonNullable<GetOrderQuoteQuery['getOrderQuote']>;
type QuoteItem = Quote['items'][number];

/* The sections that share a tab. Declared once so every member names the same
   group — a typo here would silently split a tab in two. */
const PRODUCTS_TAB = { id: 'products', label: 'Products & offer' };
const CLIENT_TAB = { id: 'client', label: 'Client & people' };
const DETAILS_TAB = { id: 'details', label: 'Details & setup' };

/**
 * Quote detail, v2.
 *
 * One column with an index down the left, instead of a main column and a
 * metadata rail. Everything a quote knows about itself is a section in the order
 * the work happens — what was asked for, what was offered, who it's for, who has
 * seen it, how it's paid, what changed — and the four numbers that decide
 * whether it needs attention are promoted out of the rail into the strip at the
 * top.
 *
 * The layout is `@codezo/quote-detail`: presentational modules that take view
 * models and slots, so the client and guest views can render the same page with
 * fewer callbacks and no margin figures. This file is the admin adapter — it
 * owns the queries, the mutations, the modals, and the formatting.
 */
export function OrderQuoteDetailPageV2() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const compactRange = useCompactMoneyRange();
	const { selectedCurrency } = useCurrencyStore();

	const [addItemsOpen, setAddItemsOpen] = useState(false);
	/* Products just added to the quote. Their rows open on arrival, because the
	   next thing an admin does with a new product is put its files on it — and
	   that only becomes possible once the line exists. */
	const [justAddedProductIds, setJustAddedProductIds] = useState<string[]>([]);
	const [deleteQuoteOpen, setDeleteQuoteOpen] = useState(false);
	const [duplicateOpen, setDuplicateOpen] = useState(false);
	/* Opens the quote in a real, read-only guest session in a new tab. Mints its
	   own single-use link on each click — see the hook. */
	const { openPreview: openGuestPreview, loading: guestPreviewLoading } = useGuestPreviewLink(
		id!
	);
	const [removeItemId, setRemoveItemId] = useState<string | null>(null);
	const [syncLineId, setSyncLineId] = useState<string | null>(null);
	const [editDetailsOpen, setEditDetailsOpen] = useState(false);
	const [editAddressOpen, setEditAddressOpen] = useState(false);
	const [editAdminsOpen, setEditAdminsOpen] = useState(false);
	const [editVettersOpen, setEditVettersOpen] = useState(false);
	const [editItem, setEditItem] = useState<EditOrderQuoteItemValue | null>(null);
	/* Which product's upload dialog is open. Held here because the control that
	   opens it is in the product's overflow menu, which this page builds, while
	   the dialog itself belongs to `QuoteItemFiles`. */
	const [uploadFilesItemId, setUploadFilesItemId] = useState<string | null>(null);
	/* The product's images, opened from a picture on its card — at the one that
	   was clicked, so the strip under the hero is worth clicking. */
	const [preview, setPreview] = useState<{
		files: FilePreviewItem[];
		index: number;
	} | null>(null);
	// What the offers could total, reported up by the offers section — it owns the
	// batches query, and re-asking for it here would pull every tier twice.
	/* "Take me to the offer" from the pending-review banner. It goes through the
	   layout because the offer section is very likely behind a closed tab, and a
	   plain scroll would land nowhere. */
	const revealSection = useRef<((sectionId: string) => void) | null>(null);

	const { data, loading, refetch, networkStatus } = useGetOrderQuoteQuery({
		variables: { id: id! },
		skip: !id,
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	});

	// An admin opening the quote lands in the view summary but never touches the
	// client-side fields the "not yet viewed" badge reads.
	useRecordQuoteView(id);

	/* The strip's money. Server-side, because min and max are each one real
	   offerable combination — plans pinned, the rest at their cheapest or dearest
	   tier — and margin percentage only means anything evaluated on the same
	   combination as the total it came from. */
	const { data: rangeData, refetch: refetchRange } = useGetOrderQuoteValueRangeQuery({
		variables: { orderQuoteId: id!, inCurrency: selectedCurrency },
		skip: !id,
		fetchPolicy: 'no-cache',
	});
	const range = rangeData?.getOrderQuoteValueRange;
	/* low and high are ordered by TOTAL and nothing else. A small-quantity tier
	   usually carries a fatter margin than a bulk one, so the cheapest combination
	   is routinely the highest-margin one — reading low.margin → high.margin
	   prints "25–18%". Every margin figure is min/max'd across both points. */
	const span = (
		pick: (point: NonNullable<NonNullable<typeof range>['low']>) => number | null
	) => {
		const a = range?.low ? pick(range.low) : null;
		const b = range?.high ? pick(range.high) : null;
		if (a == null && b == null) return [null, null] as const;
		if (a == null || b == null) {
			const only = (a ?? b) as number;
			return [only, only] as const;
		}
		return [Math.min(a, b), Math.max(a, b)] as const;
	};
	const [marginLow, marginHigh] = span((point) => point.marginAmount ?? null);
	const [pctLow, pctHigh] = span((point) => point.marginPercentage ?? null);
	const [markupLow, markupHigh] = span((point) => point.markupPercentage ?? null);
	const totalShown = compactRange(range?.low?.total ?? null, range?.high?.total ?? null);

	const { data: viewData } = useGetOrderQuoteViewSummaryQuery({
		variables: { orderQuoteId: id! },
		skip: !id,
		fetchPolicy: 'no-cache',
	});

	/* What a catalogue sync would rewrite, per line. A dry run — it writes
	   nothing — and it answers two questions at once: whether a product has
	   drifted, and when we last checked. Every line comes back, `UNCHANGED`
	   ones included, so this is keyed by item id and read per product. */
	const { data: syncData, refetch: refetchSyncPreview } = usePreviewOrderQuoteProductSyncQuery({
		variables: { orderQuoteId: id! },
		skip: !id,
		fetchPolicy: 'no-cache',
	});
	const syncLines = useMemo(
		() =>
			new Map(
				(syncData?.previewOrderQuoteProductSync?.lines ?? []).map((l) => [
					l.orderQuoteItemId,
					l,
				])
			),
		[syncData]
	);

	/* Who gets emailed is computed live, so the contacts section re-asks whenever
	   the quote or its guest links change. Every section stays mounted (hidden
	   tabs included), so a one-off fetch on mount would go stale. */
	const [recipientsVersion, bumpRecipients] = useReducer((n: number) => n + 1, 0);

	/* Everything on this page that changes the quote refreshes through here.
	   The record and the money strip are two queries but one fact: an item
	   removed, a price set, an offer sent — each moves the lifecycle stepper and
	   the value range together, and refetching only the first left the strip
	   showing the previous build's totals until a reload. */
	const refreshQuote = useCallback(() => {
		void refetch();
		void refetchRange();
		void refetchSyncPreview();
		bumpRecipients();
	}, [refetch, refetchRange, refetchSyncPreview]);

	const quote = data?.getOrderQuote;
	const isInitialLoading = loading && networkStatus === 1;
	const isEditable =
		quote?.status === OrderQuoteStatus.Draft ||
		quote?.status === OrderQuoteStatus.Requested ||
		quote?.status === OrderQuoteStatus.Pending ||
		quote?.status === OrderQuoteStatus.Submitted;

	useSetPageMeta(quote?.name || 'Quote', [
		{ label: 'Quotes', path: '/quotes' },
		{ label: quote?.name || 'Loading...' },
	]);

	const [deleteQuote, { loading: deleting }] = useDeleteOrderQuoteMutation();
	const [updateQuote, { loading: updatingAssignees }] = useUpdateOrderQuoteMutation();
	// Each batch's availableActions are resolved server-side against the quote's
	// item count — "Add products" blocks once every item already has an offer — so
	// changing the items invalidates the offers query too, not just this one.
	const [removeItem, { loading: removingItem }] = useRemoveOrderQuoteItemMutation({
		refetchQueries: ['GetOrderQuoteOfferBatches'],
	});

	const items = useMemo(() => quote?.items ?? [], [quote]);
	const quoteThreads = useQuoteThreads(id);
	const justAddedItemIds = useMemo(
		() =>
			items
				.filter((item) => justAddedProductIds.includes(item.productId))
				.map((item) => item.id),
		[items, justAddedProductIds]
	);
	const supplierLines = useMemo(
		() =>
			items.map((item) => ({
				id: item.id,
				name: item.productName || 'Unnamed product',
				sku: item.productSku,
				quantities: [...item.quantities]
					.sort((a, b) => a.quantity - b.quantity)
					.map((q) => q.quantity),
			})),
		[items]
	);

	const handleDeleteQuote = async () => {
		if (!id) return;
		try {
			await deleteQuote({ variables: { id } });
			toast.success('Quote deleted');
			navigate('/quotes');
		} catch {
			toast.error('Failed to delete quote');
		}
	};

	const handleRemoveItem = async () => {
		if (!removeItemId) return;
		try {
			await removeItem({ variables: { id: removeItemId } });
			toast.success('Item removed');
			setRemoveItemId(null);
			refreshQuote();
		} catch {
			toast.error('Failed to remove item');
		}
	};

	const handleSaveAdmins = async (ids: string[]) => {
		try {
			await updateQuote({ variables: { id: id!, input: { adminUserIds: ids } } });
			toast.success('Admins updated');
			setEditAdminsOpen(false);
			refreshQuote();
		} catch {
			toast.error('Failed to update admins');
		}
	};

	const handleSaveVetters = async (ids: string[]) => {
		try {
			await updateQuote({ variables: { id: id!, input: { vetterEntityIds: ids } } });
			toast.success('Vetters updated');
			setEditVettersOpen(false);
			refreshQuote();
		} catch {
			toast.error('Failed to update vetters');
		}
	};

	if (isInitialLoading || !quote) {
		return <DetailSkeleton />;
	}

	const statusConfig = ORDER_QUOTE_STATUS[quote.status] ?? {
		label: quote.status,
		tone: 'neutral' as const,
	};

	const stats: QuoteStat[] = [
		{
			label: 'Products',
			value: String(items.length),
			// One offer per quote now; only a quote from before can still have several.
			hint: (quote.offerBatchCount ?? 0) > 1 ? `${quote.offerBatchCount} offers` : undefined,
		},
		{
			/* Two real combinations, priced through the same summary the batch card
			   uses — not a sum of independent extremes. The range itself says it is a
			   range; a hint restating that earned nothing. */
			label: 'Offer total',
			value: totalShown,
		},
		{
			label: 'Margin',
			value: compactRange(marginLow, marginHigh),
			/* Both readings of the same profit, named. Markup is what the admin
			   typed into the pricing form, so it leads; margin is what the same
			   money is as a share of revenue, and it is the one that needs its
			   denominator spelled out — the headline above is the grand total,
			   samples and delivery included, so a reader dividing one into the
			   other gets a third number. The base is named in WORDS rather than
			   printed: a second amount only competes with the one above it, and the
			   exact figure is on the batch card. */
			hint: range?.low
				? `${formatPercentRange(markupLow, markupHigh)} markup · ${formatPercentRange(
						pctLow,
						pctHigh
					)} of product revenue`
				: 'internal · never shown to the client',
		},
		{
			label: 'Deadline',
			value: quote.deadline ? formatDate(quote.deadline) : '—',
		},
	];

	const stages: LifecycleStage[] = (quote.lifecycle ?? []).map((step) => ({
		key: step.step,
		label: step.label,
		state: step.completed ? 'done' : step.current ? 'current' : 'upcoming',
	}));

	const requested: RequestedProduct[] = items.map((item) => {
		const source = specSourceFor(item);
		const syncLine = syncLines.get(item.id);
		const rows = buildSpecificationRows(source);
		const ordered = orderedProductImages(item.productImages).filter(
			(i): i is typeof i & { url: string } => !!i.url
		);
		return {
			id: item.id,
			product: {
				id: item.productId,
				name: item.productName || 'Unknown product',
				sku: item.productSku,
			},
			/* Primary first, then the rest in their own order — the card reads this
			   as a hero and a strip of thumbnails under it. */
			media: ordered.map((img, i) => (
				<ProductImage
					key={`${item.id}-${i}`}
					image={img}
					alt={item.productName ?? undefined}
					className="h-full w-full object-cover"
				/>
			)),
			onOpenImages: ordered.length
				? (index: number) =>
						setPreview({
							files: ordered.map((img, i) => ({
								id: `${item.id}-${i}`,
								url: img.url,
								name: item.productName ?? 'Product image',
								mimeType: 'image/jpeg',
							})),
							index,
						})
				: undefined,
			quantities: item.quantities.map((q) => q.quantity.toLocaleString()),
			deadline: deadlineLabel(item.deadline),
			description: item.productDescription,
			specs: rows.map((row) => [row.label, row.value] as const),
			/* The snapshot is the only source for a quote — it is what the client was
			   shown. A line added before snapshots existed has none, and there is no
			   catalogue join left to fall back to, so say that rather than implying we
			   are showing live product data. */
			note: (
				<>
					<div>
						{item.productSpecifications
							? `Specifications as recorded on this quote ${formatDate(
									item.productSpecificationsAt!
								)}`
							: 'No specifications were recorded on this quote'}
					</div>
					{/* Drift, straight from the sync preview. The copy is the
					    backend's: it is the only side that can still compare a
					    frozen line against the catalogue. */}
					{syncLine && syncLine.kind !== OrderQuoteProductSyncKind.Unchanged && (
						<div
							className={
								syncLine.kind === OrderQuoteProductSyncKind.Unavailable
									? 'text-status-danger'
									: 'text-status-warn'
							}
						>
							{syncLine.message}
						</div>
					)}
				</>
			),
			primaryAction: item.productId ? (
				<Button
					variant="outline"
					size="sm"
					onClick={() => navigate(`/products/${item.productId}`)}
				>
					<ArrowUpRight size={12} data-icon="inline-start" />
					Open product
				</Button>
			) : undefined,
			/* The line's files, not the offer's: every offer made for this product
			   serves this same list, so this is the one place they are added and
			   removed. */
			files: (
				<QuoteItemFiles
					itemId={item.id}
					files={item.attachments ?? []}
					canEdit={isEditable}
					onChanged={refreshQuote}
					uploadOpen={uploadFilesItemId === item.id}
					onUploadOpenChange={(open) => setUploadFilesItemId(open ? item.id : null)}
				/>
			),
			/* The menu outlives editability: a sent or accepted quote can still be
			   synced against the catalogue, which is usually exactly when you want
			   to. Only the edits are gated. */
			/* The comments button sits beside the menu, never inside it — unread
			   messages have to be visible on the row, not behind a click. */
			actions: (
				<span className="flex items-center gap-0.5">
					<ProductCommentsButton
						{...quoteThreads.summaryFor(item.id)}
						onClick={() => quoteThreads.open(item.id)}
					/>
					{isEditable || syncLine ? (
						<DropdownMenu>
							<DropdownMenuTrigger className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1">
								<MoreHorizontal size={14} />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{/* Uploading lives here rather than beside the file chips: one
						    control per product, in the place every other thing done TO
						    the product is done. */}
								{isEditable && (
									<>
										<DropdownMenuItem
											onClick={() => setUploadFilesItemId(item.id)}
										>
											<Upload size={14} /> Add files
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() =>
												setEditItem({
													id: item.id,
													productName: item.productName || 'Unknown',
													quantities: item.quantities.map((q) => ({
														id: q.id,
														quantity: q.quantity,
													})),
													deadline: item.deadline ?? null,
												})
											}
										>
											<Pencil size={14} /> Edit item
										</DropdownMenuItem>
									</>
								)}
								{/* Shown even when there is nothing to pull in: "already
							    current" is the answer an admin came to the menu for, and
							    hiding it just sends them looking. The reason is rendered,
							    not a title — a disabled item is pointer-events-none, so a
							    tooltip on it never fires. */}
								{syncLine && (
									<DropdownMenuItem
										disabled={!SYNCABLE_KINDS.has(syncLine.kind)}
										onClick={() => setSyncLineId(item.id)}
									>
										<RefreshCw size={14} />
										<span className="flex flex-col gap-0.5">
											<span>
												{syncLine.kind ===
												OrderQuoteProductSyncKind.NeverSnapshotted
													? 'Fill from catalogue'
													: 'Sync from catalogue'}
											</span>
											{!SYNCABLE_KINDS.has(syncLine.kind) && (
												<span className="text-2xs text-text-4">
													{syncLine.message}
												</span>
											)}
										</span>
									</DropdownMenuItem>
								)}
								{isEditable && (
									<DropdownMenuItem
										variant="destructive"
										onClick={() => setRemoveItemId(item.id)}
									>
										<Trash2 size={14} /> Remove
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					) : null}
				</span>
			),
		};
	});

	const viewSummary = viewData?.getOrderQuoteViewSummary;

	/* Every section of the page, declared once. `QuoteSections` decides where
	   each one lands — down one column beside the index rail, or behind its tab.
	   `tabGroup` is what the tab strip reads: nine tabs is a scrollbar, so they
	   fold into four by who the section is about. What was asked for and what was
	   offered share the first — you price against the request, so reading one
	   without the other is the wrong unit of work. Everyone attached to the quote
	   shares the second, the client and the two 05-side roles alike. What is left
	   is how the quote itself is set up. Order inside a tab follows this list, and
	   the rail still lists all nine separately. */
	const sections: QuoteLayoutSection[] = [
		{
			id: 'requested',
			label: 'Requested products',
			count: items.length,
			tabGroup: PRODUCTS_TAB,
			content: (
				<>
					<SectionHead
						title="Requested products"
						meta="what the client asked to have priced"
						action={
							isEditable ? (
								<Button
									variant="outline"
									size="sm"
									onClick={() => setAddItemsOpen(true)}
								>
									<Plus size={12} data-icon="inline-start" />
									Add products
								</Button>
							) : undefined
						}
					/>
					<RequestedProducts
						items={requested}
						openIds={justAddedItemIds}
						emptyState={
							<div className="px-4 py-11 text-center text-sm text-text-4">
								{isEditable
									? 'Add products to this quote to get started.'
									: 'No products were added to this quote.'}
							</div>
						}
					/>
				</>
			),
		},
		{
			id: 'offers',
			label: 'Offer',
			count: quote.offerBatchCount ?? 0,
			tabGroup: PRODUCTS_TAB,
			content: (
				<>
					<SectionHead
						title="Offer"
						meta="one build — the client combines quantities, delivery and samples however they want"
					/>
					<OrderQuoteOffers
						quoteId={id!}
						quoteItems={items}
						hasClientContacts={quote.clientContactUserIds.length > 0}
						quoteDeadline={quote.deadline}
						editable={isEditable}
						skip={false}
						renderBatch={(props) => <OfferBatchV2 {...props} />}
						onQuoteChanged={refreshQuote}
					/>
				</>
			),
		},
		{
			id: 'suppliers',
			label: 'Supplier prices',
			tabGroup: PRODUCTS_TAB,
			content: (
				<>
					<SectionHead
						title="Supplier prices"
						meta="price requests sent by link · use a supplier's prices on a draft offer"
					/>
					<QuoteSuppliers
						quoteId={id!}
						lines={supplierLines}
						editable={isEditable}
						onPricesApplied={refreshQuote}
					/>
				</>
			),
		},
		{
			id: 'client',
			label: 'Client',
			tabGroup: CLIENT_TAB,
			content: (
				<>
					<SectionHead
						title="Client"
						meta="who this quote is for · guest links open the whole quote"
					/>
					{quote.organization ? (
						<QuoteClientV2
							quoteId={id!}
							organization={quote.organization}
							onOpenClient={() => navigate(`/clients/${quote.organization!.id}`)}
							onGuestLinksChanged={bumpRecipients}
						/>
					) : (
						<Card padded>
							<span className="text-sm text-text-4">
								This quote isn’t attached to a client.
							</span>
						</Card>
					)}
				</>
			),
		},
		...(quote.organizationId
			? [
					{
						id: 'client-contacts',
						label: 'Client contacts',
						count: quote.clientContactUserIds.length,
						tabGroup: CLIENT_TAB,
						content: (
							<QuoteClientContactsV2
								orderQuoteId={id!}
								organizationId={quote.organizationId}
								organizationName={quote.organization?.name}
								clientContactUserIds={quote.clientContactUserIds}
								recipientsVersion={recipientsVersion}
								onSuccess={refreshQuote}
							/>
						),
					},
				]
			: []),
		{
			id: 'views',
			label: 'Quote views',
			tabGroup: CLIENT_TAB,
			content: (
				<>
					<SectionHead
						title="Quote views"
						meta="who has opened this quote · client views come from guest links"
					/>
					<QuoteViews
						viewed={!!quote.lastClientViewedAt}
						headline={
							quote.lastClientViewedAt
								? `Client viewed ${timeAgo(quote.lastClientViewedAt)}`
								: 'The client has not viewed this quote yet'
						}
						sub={
							quote.lastClientViewedAt
								? [
										`${quote.clientViewCount} client ${
											quote.clientViewCount === 1 ? 'view' : 'views'
										}`,
										quote.firstClientViewedAt &&
											`first on ${formatDate(quote.firstClientViewedAt)}`,
									]
										.filter(Boolean)
										.join(' · ')
								: null
						}
						/* Everyone, admins included — deliberately a different question
						   from the client counts above, so the two are expected to
						   disagree. */
						summary={
							viewSummary
								? `${viewSummary.totalViews} ${
										viewSummary.totalViews === 1 ? 'view' : 'views'
									} · ${viewSummary.distinctViewers} ${
										viewSummary.distinctViewers === 1 ? 'person' : 'people'
									}`
								: null
						}
						viewers={(viewSummary?.viewers ?? []).map((viewer) => {
							const email = viewer.user?.email ?? null;
							// Guest-link members carry a placeholder org-user name
							// ("Guest"), so the email is often all that identifies them.
							const name = fullName(viewer.user?.organizationUser) ?? email;
							return {
								id: viewer.id,
								name: name ?? 'Unknown user',
								email,
								role: [
									viewer.actorRole,
									CHANNEL_LABEL[viewer.lastChannel] ?? viewer.lastChannel,
								]
									.filter(Boolean)
									.join(' · '),
								viewCount: viewer.viewCount,
								lastViewed: timeAgo(viewer.lastViewedAt),
								avatar: <InitialsAvatar name={name ?? 'Unknown user'} size={28} />,
							};
						})}
					/>
				</>
			),
		},
		{
			id: 'details',
			label: 'Details',
			tabGroup: DETAILS_TAB,
			content: (
				<>
					{/* No action on the heading: it spans two cards, and the address
					    beside these facts is set on its own. An Edit up here read as
					    covering both. */}
					<SectionHead title="Details" meta="purpose, timing and where it ships" />
					<div className="grid gap-3 md:grid-cols-2">
						<QuoteFacts
							title="Quote details"
							action={
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setEditDetailsOpen(true)}
								>
									<Pencil size={11} data-icon="inline-start" />
									Edit
								</Button>
							}
							facts={[
								{
									label: 'Purpose',
									value: quote.purpose
										? quote.purposeOther
											? `${quote.purpose.replace(/_/g, ' ').toLowerCase()} — ${quote.purposeOther}`
											: quote.purpose.replace(/_/g, ' ').toLowerCase()
										: 'Not specified',
									muted: !quote.purpose,
								},
								{
									label: 'Deadline',
									value: quote.deadline ? formatDate(quote.deadline) : '—',
									muted: !quote.deadline,
								},
								{
									label: 'Description',
									value: quote.description || 'No description',
									muted: !quote.description,
								},
							]}
						/>
						<QuoteAddress
							action={
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setEditAddressOpen(true)}
								>
									{quote.deliveryAddress ? (
										<Pencil size={11} data-icon="inline-start" />
									) : (
										<Plus size={11} data-icon="inline-start" />
									)}
									{quote.deliveryAddress ? 'Edit' : 'Set address'}
								</Button>
							}
							lines={
								quote.deliveryAddress
									? [
											quote.deliveryAddress.addressLine1,
											quote.deliveryAddress.addressLine2,
											[
												quote.deliveryAddress.postalCode,
												quote.deliveryAddress.city,
											]
												.filter(Boolean)
												.join(' '),
											quote.deliveryAddress.countryCode,
										].filter((line): line is string => !!line)
									: []
							}
						/>
					</div>
				</>
			),
		},
		{
			id: 'managers',
			label: 'Managers',
			count: quote.adminUsers?.length ?? 0,
			tabGroup: CLIENT_TAB,
			content: (
				<>
					<SectionHead
						title="Managers"
						meta="who runs this quote on the 05 side"
						action={
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setEditAdminsOpen(true)}
							>
								<UserPlus size={12} data-icon="inline-start" />
								Assign
							</Button>
						}
					/>
					<QuotePeople
						people={(quote.adminUsers ?? []).map((admin) => ({
							id: admin.id,
							name: `${admin.firstName} ${admin.lastName}`,
							meta: 'Assigned admin',
							avatar: (
								<InitialsAvatar
									name={`${admin.firstName} ${admin.lastName}`}
									size={26}
								/>
							),
						}))}
						emptyLabel="No admins assigned — nobody owns this quote yet."
					/>
				</>
			),
		},
		{
			id: 'vetters',
			label: 'Vetters',
			count: quote.vetterEntities?.length ?? 0,
			tabGroup: CLIENT_TAB,
			content: (
				<>
					<SectionHead
						title="Vetters"
						meta="independent review before the offer goes out"
						action={
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setEditVettersOpen(true)}
							>
								<UserPlus size={12} data-icon="inline-start" />
								Assign
							</Button>
						}
					/>
					<QuotePeople
						people={(quote.vetterEntities ?? []).map((vetter) => ({
							id: vetter.id,
							name: vetter.name,
							avatar: <Shield size={14} className="shrink-0 text-text-4" />,
						}))}
						emptyIcon={<Shield size={14} className="shrink-0 text-text-4" />}
						emptyLabel="No vetters assigned — this quote ships without external review."
					/>
				</>
			),
		},
		{
			id: 'payment',
			label: 'Payment settings',
			tabGroup: DETAILS_TAB,
			content: (
				<>
					<SectionHead title="Payment settings" meta="how this quote is paid for" />
					<QuoteSettingsV2
						orderQuoteId={id!}
						settings={{
							productPaymentMode: quote.productPaymentMode,
							samplePaymentMode: quote.samplePaymentMode,
							stripeCheckoutLimit: quote.stripeCheckoutLimit,
							invoiceMinimum: quote.invoiceMinimum,
							paymentSettingsSnapshotAt: quote.paymentSettingsSnapshotAt,
						}}
						onSuccess={refreshQuote}
					/>
				</>
			),
		},
		{
			id: 'activity',
			label: 'Activity',
			content: (
				<>
					<SectionHead title="Activity" meta="every change is tracked" />
					<QuoteActivityV2 quoteId={id!} />
				</>
			),
		},
	];

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			{/* The way back, as every other detail page in the app has it — the
			    order page and the client's own quote page both open with this row.
			    This page was the one without it: the header is sticky, so scrolling
			    up returned the title and nothing to leave by, and the only way out
			    was the browser's own control.

			    `mb-2`, not the `mb-4` the order page uses: `QuoteHeader` opens with
			    a 16px sentinel of its own, and the two together were reading as a
			    gap twice the size of the order page's. */}
			<button
				onClick={() => navigate(-1)}
				className="mb-2 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
			>
				<ArrowLeft size={14} />
				Quotes
			</button>

			<QuoteHeader
				name={quote.name}
				status={statusConfig}
				subtitle={
					/* Client · reference · created, as the design has it. The design's
					   project link is gone with the Projects feature.
					 *
					 * The reference is the head of the uuid: orders carry a real
					 * `orderNumber` and quotes carry nothing, so until the API grows a
					 * `quoteNumber` this is the only handle a person can quote back at
					 * you. Full id on hover. */
					<>
						{quote.organization?.name && <span>{quote.organization.name} · </span>}
						<span className="font-mono" title={quote.id}>
							{quote.id.slice(0, 8).toUpperCase()}
						</span>
						{quote.createdAt && <span> · Created {formatDate(quote.createdAt)}</span>}
					</>
				}
				actions={
					/* Outline, outline, primary, overflow. The offer action is the
					   primary one because building the offer is what this page is for;
					   delete is the only thing left behind the overflow.
					 *
					 * Previewing is a header action rather than an overflow item
					 * because it is the one way to check what was actually sent, and
					 * it was going unused where it sat. It is not gated on
					 * `isEditable`: a quote stops being editable the moment it
					 * matters most to see what the client sees. */
					<>
						{isEditable && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => setEditDetailsOpen(true)}
							>
								<Pencil size={14} data-icon="inline-start" />
								Edit quote
							</Button>
						)}
						<Button
							variant="outline"
							size="sm"
							onClick={openGuestPreview}
							disabled={guestPreviewLoading}
						>
							<Eye size={14} data-icon="inline-start" />
							{guestPreviewLoading ? 'Opening…' : 'Preview as guest'}
						</Button>
						{/* Duplicate holds the menu open on every quote — copying a sent or
						    accepted one into a fresh draft is the point of it, so it is
						    deliberately not gated on `isEditable` the way delete is. */}
						<DropdownMenu>
							<DropdownMenuTrigger className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1">
								<MoreHorizontal size={14} />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => setDuplicateOpen(true)}>
									<Copy size={14} /> Duplicate
								</DropdownMenuItem>
								{isEditable && (
									<DropdownMenuItem
										variant="destructive"
										onClick={() => setDeleteQuoteOpen(true)}
									>
										<Trash2 size={14} /> Delete
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					</>
				}
			/>

			<QuoteOverview stats={stats} stages={stages} />

			<QuoteSections
				sections={sections}
				revealRef={revealSection}
				/* Post-deadline accept awaiting an admin's answer — above the strip,
				   because nothing else on the page matters until it's resolved,
				   including whichever tab happens to be open. */
				lead={
					quote.status === OrderQuoteStatus.PendingReview &&
					quote.pendingAcceptance && (
						<PendingAcceptanceReview
							quoteId={id!}
							quoteItems={items}
							pendingAcceptance={quote.pendingAcceptance}
							supersededAcceptances={quote.supersededAcceptances}
							onResolved={refreshQuote}
							onRevise={() => revealSection.current?.('offers')}
						/>
					)
				}
			/>

			{/* ── Modals ── */}
			{quoteThreads.openItemId && (
				<ProductThreadsPanel
					productName={
						items.find((i) => i.id === quoteThreads.openItemId)?.productName ??
						'Product'
					}
					threads={quoteThreads.openThreads}
					activeThread={quoteThreads.activeThread}
					unreadOf={quoteThreads.unreadOf}
					onTabChange={quoteThreads.setActiveThread}
					onClose={quoteThreads.close}
					onRead={quoteThreads.markLocallyRead}
				/>
			)}

			<AddProductsToQuoteModal
				open={addItemsOpen}
				onOpenChange={setAddItemsOpen}
				quoteId={id!}
				organizationId={quote.organizationId}
				organizationName={quote.organization?.name}
				existingProductIds={items.map((i) => i.productId)}
				onSuccess={(addedProductIds) => {
					setJustAddedProductIds(addedProductIds);
					refreshQuote();
				}}
			/>

			<DuplicateOrderQuoteModal
				quote={
					duplicateOpen
						? {
								id: quote.id,
								name: quote.name,
								organizationId: quote.organizationId,
								organizationName: quote.organization?.name,
							}
						: null
				}
				onOpenChange={setDuplicateOpen}
				onDuplicated={(newQuoteId) => {
					setDuplicateOpen(false);
					navigate(`/quotes/${newQuoteId}`);
				}}
			/>

			<ConfirmDialog
				open={deleteQuoteOpen}
				onOpenChange={setDeleteQuoteOpen}
				title="Delete quote"
				description={`Are you sure you want to delete "${quote.name}"? This action cannot be undone.`}
				confirmLabel="Delete"
				loading={deleting}
				onConfirm={handleDeleteQuote}
			/>

			<ConfirmDialog
				open={!!removeItemId}
				onOpenChange={(v) => !v && setRemoveItemId(null)}
				title="Remove item"
				description="Are you sure you want to remove this item from the quote?"
				confirmLabel="Remove"
				loading={removingItem}
				onConfirm={handleRemoveItem}
			/>

			<EditOrderQuoteDetailsModal
				open={editDetailsOpen}
				onOpenChange={setEditDetailsOpen}
				quoteId={id!}
				initialValues={{
					name: quote.name,
					description: quote.description,
					purpose: quote.purpose,
					purposeOther: quote.purposeOther,
					deadline: quote.deadline,
					organizationId: quote.organizationId,
					organizationName: quote.organization?.name,
				}}
				offerBatchCount={quote.offerBatchCount}
				onSuccess={refreshQuote}
			/>

			<EditOrderQuoteAddressModal
				open={editAddressOpen}
				onOpenChange={setEditAddressOpen}
				quoteId={id!}
				organizationId={quote.organizationId}
				initialValue={quote.deliveryAddress ?? null}
				onSuccess={refreshQuote}
			/>

			<AdminUserPickerModal
				open={editAdminsOpen}
				onOpenChange={setEditAdminsOpen}
				selectedIds={quote.adminUserIds ?? []}
				onSave={handleSaveAdmins}
				loading={updatingAssignees}
			/>

			<VetterPickerModal
				open={editVettersOpen}
				onOpenChange={setEditVettersOpen}
				selectedIds={quote.vetterEntityIds ?? []}
				onSave={handleSaveVetters}
				loading={updatingAssignees}
			/>

			{preview && (
				<FilePreview
					files={preview.files}
					initialIndex={preview.index}
					onClose={() => setPreview(null)}
				/>
			)}

			<EditOrderQuoteItemModal
				open={!!editItem}
				onOpenChange={(open) => {
					if (!open) setEditItem(null);
				}}
				item={editItem}
				onSuccess={refreshQuote}
			/>

			<SyncProductFromCatalogueModal
				orderQuoteId={id!}
				line={(syncLineId && syncLines.get(syncLineId)) || null}
				onOpenChange={(open) => {
					if (!open) setSyncLineId(null);
				}}
				onSynced={refreshQuote}
			/>
		</div>
	);
}

/* ── helpers ── */

/**
 * Only these two kinds have anything to pull in. `UNCHANGED` already matches the
 * catalogue and `UNAVAILABLE` cannot be read at all — for a deleted product that
 * is permanent, not a retry.
 */
const SYNCABLE_KINDS: ReadonlySet<OrderQuoteProductSyncKind> = new Set([
	OrderQuoteProductSyncKind.Changed,
	OrderQuoteProductSyncKind.NeverSnapshotted,
]);

const CHANNEL_LABEL: Record<string, string> = {
	ADMIN_PORTAL: 'Admin portal',
	CLIENT_PORTAL: 'Client portal',
	GUEST_LINK: 'Guest link',
};

/** Guests can arrive with a blank half, so join on what's actually there. */
function fullName(user: { firstName: string; lastName: string } | null | undefined): string | null {
	if (!user) return null;
	const name = [user.firstName, user.lastName]
		.map((part) => part.trim())
		.filter(Boolean)
		.join(' ');
	return name || null;
}

/**
 * What to show for a line's specifications. The snapshot is the only source for
 * a quote — it is what the client was shown — and a line added before snapshots
 * existed has none, with no catalogue join left to fall back to.
 */
function specSourceFor(item: QuoteItem) {
	return {
		specifications: item.productSpecifications,
		dimensions: item.productDimensions,
		weight: item.productWeight,
	};
}

/**
 * Colour comes from the API's own urgency level, never from a date compared
 * here. `UNKNOWN` means unverified rather than fine, so it reads neutral and
 * never green.
 */
/** The requested date and how many days are left. Neutral — it is a record of
 * what the client asked for, not a status. */
function deadlineLabel(deadline?: string | null): string | null {
	if (!deadline) return null;
	const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / 86_400_000);
	const countdown =
		days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? 'Due today' : `${days}d left`;
	return `${countdown} · ${formatDate(deadline)}`;
}

/**
 * The page's own chrome, empty — the title, the subtitle and the stat strip,
 * which are the page's to size. The section area below them is the package's
 * `QuoteSectionsSkeleton`, drawn to the same measurements as the real layouts so
 * the page loads into the shape it is about to show.
 */
function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<div className="mb-2 h-7 w-56 animate-pulse rounded bg-surface-fill" />
			<div className="mb-6 h-4 w-72 animate-pulse rounded bg-surface-fill" />
			<div className="h-[130px] animate-pulse rounded-[var(--r-3)] bg-surface-fill" />
			<QuoteSectionsSkeleton />
		</div>
	);
}
